import React from 'react'
import 'lib/fontawesomeLibrary'
import { ApolloProvider } from '@apollo/client'
import { Router } from 'react-router-dom'
import { withErrorBoundary } from 'lib/errorBoundary'
import { detect } from 'detect-browser'
import { setInfoBreadcrumb, setupSentry } from 'lib/sentry'
import { useAlmaSammio } from 'lib/alma/hooks/useAlmaSammio'
import { useTranslation } from 'react-i18next'
import { history } from 'lib/utils/store/navHistory'
import AppVersionHandler from 'lib/appVersionHandler'
import { logException } from 'lib/errors'

import Client from 'components/Client'
import AppKeyValuesLogic from './AppKeyValuesLogic'
import AppRouter from './AppRouter'
import AddToHomePrompt from './AddToHome/AddToHomePrompt'
import AppWindowBreakpoints from './Util/WindowBreakpoints/AppWindowBreakpoints'
import DebugTools from './Util/DebugTools/DebugTools'
import useAlmaCmp from 'lib/alma/hooks/useAlmaCmp'
import useABTasty from 'lib/alma/hooks/useABTasty'
import Auth from './Auth/Auth'
import { withProfiler } from '@sentry/react'
import { flowRight } from 'lodash'

const browser = detect()

export const AppRootContext = React.createContext()

console.log('App initializing....')

const App = () => {
    useAlmaSammio()
    useABTasty()
    const cmp = useAlmaCmp()

    const { i18n } = useTranslation()

    if (cmp.featureConsents.sentry) {
        setupSentry()
    }

    try {
        window.__mv_appVersionHandler = new AppVersionHandler()
    } catch (e) {
        setInfoBreadcrumb('AppVersionHandler failed to initialize.')
        logException(e)
    }

    return (
        <DebugTools>
            <ApolloProvider client={Client}>
                <Auth
                    language={i18n.language}
                    history={history}
                    allowGa={cmp.featureConsents.googleAnalytics}
                >
                    <AppWindowBreakpoints
                        render={(breakpoints) => (
                            <AppRootContext.Provider
                                value={{
                                    browser,
                                    breakpoints,
                                    cmp,
                                }}
                            >
                                <AddToHomePrompt>
                                    <AppKeyValuesLogic>
                                        <Router history={history}>
                                            <AppRouter />
                                        </Router>
                                    </AppKeyValuesLogic>
                                </AddToHomePrompt>
                            </AppRootContext.Provider>
                        )}
                    />
                </Auth>
            </ApolloProvider>
        </DebugTools>
    )
}

export default flowRight(
    withErrorBoundary,
)(withProfiler(App, { name: 'App' }))
